import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import AppStoreButtons from "./AppStoreButtons";

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const hasShownRef = useRef(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return;
    const previousFocus = document.activeElement;
    const modal = modalRef.current;
    const focusable = () => [...modal.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')];
    const focusFirst = () => (focusable()[0] || modal).focus();
    focusFirst();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setIsVisible(false);
      } else if (event.key === 'Tab') {
        const elements = focusable();
        const first = elements[0] || modal;
        const last = elements[elements.length - 1] || modal;
        if (event.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    const onFocusIn = (event) => { if (!modal.contains(event.target)) focusFirst(); };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn);
      if (previousFocus?.isConnected && typeof previousFocus.focus === 'function') previousFocus.focus();
    };
  }, [isVisible]);

  const showPopup = () => {
    if (!hasShownRef.current) {
      setIsVisible(true);
      hasShownRef.current = true;
    }
  };

  useEffect(() => {
    // Touch visitors should be able to scroll, use forms and navigate back
    // without a promotional overlay interrupting their task.
    if (window.matchMedia('(pointer: coarse)').matches || window.innerWidth <= 900) return;

    // Intentionally NO sessionStorage persistence — hasShownRef already
    // prevents showing twice per page load, and relying on sessionStorage
    // meant the popup never re-appeared across reloads, making it look
    // broken. Every fresh page load now gets a fresh chance to trigger.

    // 1. Mouse leaves toward top of screen (desktop)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 10) {
        showPopup();
      }
    };

    // 2. Mouse moves rapidly toward top (catching quick exits)
    let lastY = 0;
    const handleMouseMove = (e) => {
      const velocity = lastY - e.clientY;
      // If moving up fast and near the top
      if (velocity > 50 && e.clientY < 100) {
        showPopup();
      }
      lastY = e.clientY;
    };

    // Add event listeners after a short delay (don't show immediately on page load)
    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
      document.addEventListener("mousemove", handleMouseMove);

    }, 2000); // Reduced to 2 seconds

    return () => {
      clearTimeout(timer);

      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mousemove", handleMouseMove);

    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="exit-popup-overlay" onClick={handleClose}>
      <div className="exit-popup-modal" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="exit-popup-title" aria-describedby="exit-popup-description" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <button className="exit-popup-close" onClick={handleClose} aria-label="Close">
          <X size={20} />
        </button>

        <div className="exit-popup-header">
          <h2 id="exit-popup-title">Before you go…</h2>
        </div>

        <div className="exit-popup-body">
          <p id="exit-popup-description">
            Thryvin&apos; is live on the App Store and Google Play.<br />
            Download it free and start training with your AI coach today.
          </p>
          <p className="exit-popup-bonus">
            Available on iPhone and Android.
          </p>
        </div>

        <div className="exit-popup-cta">
          <AppStoreButtons />
        </div>

        <button className="exit-popup-dismiss" onClick={handleClose}>
          No thanks
        </button>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
